/**
 * V3 workspace shell — premium AI-Operating-System chrome.
 *
 * Design system:
 *   • Sidebar    → dark charcoal (#0B1220), white text, gold active accent
 *   • Top bar    → white, thin gold accent bar underneath, generous spacing
 *   • Content    → soft slate-50 canvas, cards use larger radius + soft shadow
 *   • Motion     → subtle transform/opacity, not flashy
 *
 * IA is preserved from the prior shell (Dashboard, CRM, Intelligence Engines[5],
 * Strategy Center[4], AI Center, Business, Administration). Both global and
 * per-group collapse persist to localStorage; favorites work as before.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Brain,
  Layers as LayersIcon,
  Sparkles,
  Briefcase,
  Shield,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Star,
  StarOff,
  Search,
  Bell,
  Building2,
  Menu,
  X,
  Calculator,
  Wrench,
  BarChart3,
  Target,
  Wand2,
  Handshake,
  Home,
  Hammer,
  KeyRound,
  LogOut,
  LifeBuoy,
  FileText,
  FileSignature,
  ShoppingCart,
  Coins,
  Megaphone,
  Send,
  Mail,
  BookOpen,
  Mic,
  MousePointerClick,
  Wrench as WrenchIcon,
  Calculator as CalcIcon,
  Building,
  CheckSquare,
  Gauge,
  Settings as SettingsIcon,
  Share2,
  HeartHandshake,
  Map as MapIcon,
  Flame,
  Users as UsersGroupIcon,
  MessageCircle,
  Award,
  Trophy,
  TrendingUp,
  Crown,
  CalendarDays,
  Footprints,
  Zap,
  ChevronDown as CaretDown,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { MODULE_THEME } from "@/pages/v3/engineTheme";
import DealWonModal from "@/components/DealWonModal";
import SupportWidget from "@/components/SupportWidget";
import JarvisFAB from "@/components/v3/JarvisFAB";
import BetaFrictionFab from "@/components/BetaFrictionFab";
import WorkspaceFocusChip from "@/components/WorkspaceFocusChip";
import ActivePropertyProvider, { useActiveProperty } from "@/context/ActivePropertyContext";
import ActivePropertyPill from "@/components/v3/ActivePropertyPill";
import SeminarCountdownChip from "@/components/v3/SeminarCountdownChip";
import DemoModeBanner from "@/components/v3/DemoModeBanner";
import OnboardingTour from "@/components/v3/OnboardingTour";

// ──────────────────────────────────────────────────────────────
// Navigation model
// ──────────────────────────────────────────────────────────────
// ──────────────────────────────────────────────────────────────
// Navigation model
//
// 2026-08-15 · Restructured into two explicit workflow sections
// (ACQUISITIONS = filling the pipeline · DISPOSITIONS = clearing it)
// with general/utility items outside. Section headers are rendered as
// small-caps dividers so the sidebar's information hierarchy matches
// the operator's mental model.
// ──────────────────────────────────────────────────────────────
const NAV = [
  // ── Top-level (unchanged) ────────────────────────────────────
  { kind: "leaf",  to: "/v3", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "v3-nav-dashboard" },
  { kind: "leaf",  to: "/v3/calendar",   label: "Calendar",  icon: CalendarDays, testid: "v3-nav-calendar" },

  // ── ACQUISITIONS ─────────────────────────────────────────────
  { kind: "section", label: "Acquisitions", icon: Target, testid: "v3-nav-section-acquisitions" },
  { kind: "leaf", to: "/v3/crm/marketplace", label: "Lead Marketplace", icon: ShoppingCart, testid: "v3-nav-crm-marketplace" },
  { kind: "group", id: "crm", label: "CRM", icon: Users, testid: "v3-nav-group-crm",
    items: [
      { to: "/v3/crm/leads",       label: "Leads",        icon: Users,      testid: "v3-nav-crm-leads" },
      { to: "/v3/crm/discover",    label: "Discover",     icon: Brain,      testid: "v3-nav-crm-discover" },
      { to: "/v3/crm/dialer",      label: "Power Dialer", icon: Handshake,  testid: "v3-nav-crm-dialer" },
      { to: "/v3/crm/pipeline",    label: "Pipeline",     icon: LayersIcon, testid: "v3-nav-crm-pipeline" },
      { to: "/v3/crm/ai-deals",    label: "AI Deals",     icon: Sparkles,   testid: "v3-nav-crm-ai-deals", ai: true, badge: "NEW" },
      { to: "/v3/crm/inbox",       label: "Inbox",        icon: Bell,       testid: "v3-nav-crm-inbox" },
    ] },
  { kind: "group", id: "outreach", label: "Outreach", icon: Megaphone, testid: "v3-nav-group-outreach",
    items: [
      { to: "/v3/crm/outreach/sequences",     label: "Sequences",       icon: Send,              testid: "v3-nav-outreach-sequences" },
      { to: "/v3/crm/outreach/drips",         label: "Drip Campaigns",  icon: Mail,              testid: "v3-nav-outreach-drips" },
      { to: "/v3/crm/outreach/drip-library",  label: "Drip Library",    icon: BookOpen,          testid: "v3-nav-outreach-drip-library" },
      { to: "/v3/crm/outreach/scripts",       label: "Call Scripts",    icon: FileText,          testid: "v3-nav-outreach-scripts" },
      { to: "/v3/crm/outreach/marketing",     label: "Channel ROI",     icon: BarChart3,         testid: "v3-nav-outreach-marketing" },
      { to: "/v3/crm/outreach/captures",      label: "Lead Captures",   icon: MousePointerClick, testid: "v3-nav-outreach-captures" },
      { to: "/v3/crm/outreach/recordings",    label: "Call Recordings", icon: Mic,               testid: "v3-nav-outreach-recordings" },
    ] },
  // 2026-09-06 — Intelligence + Strategy Center groups collapsed.
  //   Every intel + strategy page (Wholesale Engine, Repair Intelligence,
  //   Comps & ARV, Deal Score, Acquisition/Offer/Exit Intel, AI Recs,
  //   Fix & Flip, BRRRR, Creative Finance) now lives as a tab inside the
  //   Deal Card at /v3/properties/:id. Standalone routes stay live for
  //   direct-link/back-compat but no longer compete for sidebar real estate.
  //   Kept the general-purpose Deal Analyzer as a single leaf for anyone
  //   who wants to run numbers before saving a property.
  { kind: "leaf", to: "/v3/tools/analyzer", label: "Deal Analyzer", icon: CalcIcon, testid: "v3-nav-tools-analyzer" },

  // ── DISPOSITIONS ─────────────────────────────────────────────
  { kind: "section", label: "Boots on the Ground", icon: Footprints, testid: "v3-nav-section-bog" },
  { kind: "leaf", to: "/v3/field-services", label: "Field Services", icon: Footprints, testid: "v3-nav-bog-field-services", badge: "NEW" },

  // ── DISPOSITIONS ─────────────────────────────────────────────
  { kind: "section", label: "Dispositions", icon: KeyRound, testid: "v3-nav-section-dispositions" },
  { kind: "leaf", to: "/v3/crm/pipeline?ready=1", label: "Match & Blast", icon: Zap, testid: "v3-nav-match-blast", liveBadgeSource: "readyToBlast" },
  { kind: "group", id: "buyers", label: "Buyer Network", icon: Users, testid: "v3-nav-group-buyers",
    items: [
      { to: "/v3/crm/buyers",     label: "Buyers",     icon: Users, testid: "v3-nav-crm-buyers" },
      { to: "/v3/crm/deal-rooms", label: "Deal Rooms", icon: Home,  testid: "v3-nav-crm-deal-rooms" },
    ] },
  { kind: "leaf", to: "/v3/crm/contracts", label: "Contracts", icon: FileText, testid: "v3-nav-crm-contracts" },
  { kind: "leaf", to: "/v3/dispo/transactions", label: "Transactions", icon: FileSignature, testid: "v3-nav-dispo-transactions", badge: "NEW" },
  { kind: "leaf", to: "/v3/crm/closings",  label: "Closings",  icon: KeyRound, testid: "v3-nav-crm-closings" },

  // ── GENERAL (unchanged utilities) ────────────────────────────
  { kind: "section", label: "General", testid: "v3-nav-section-general" },
  { kind: "group", id: "tools", label: "Operator Tools", icon: WrenchIcon, testid: "v3-nav-group-tools",
    items: [
      { to: "/v3/tools/portfolio",      label: "Portfolio",       icon: Building,     testid: "v3-nav-tools-portfolio" },
      { to: "/v3/tools/tasks",          label: "Tasks",           icon: CheckSquare,  testid: "v3-nav-tools-tasks" },
      { to: "/v3/tools/command-center", label: "Command Center",  icon: Gauge,        testid: "v3-nav-tools-command-center" },
      { to: "/v3/markets",              label: "Markets",         icon: MapIcon,      testid: "v3-nav-tools-markets" },
      { to: "/v3/markets/heatmap",      label: "Heatmap",         icon: Flame,        testid: "v3-nav-tools-heatmap" },
    ] },
  { kind: "group", id: "growth", label: "Growth", icon: HeartHandshake, testid: "v3-nav-group-growth",
    items: [
      { to: "/v3/growth/wins",        label: "Wins",         icon: Trophy,         testid: "v3-nav-growth-wins" },
      { to: "/v3/growth/leaderboard", label: "Leaderboard",  icon: Award,          testid: "v3-nav-growth-leaderboard" },
      { to: "/v3/growth/referrals",   label: "Referrals",    icon: Share2,         testid: "v3-nav-growth-referrals" },
      { to: "/v3/growth/affiliate",   label: "Affiliates",   icon: HeartHandshake, testid: "v3-nav-growth-affiliate" },
    ] },
  // 2026-07-31 · Community group intentionally removed from the daily nav
  // (Tracy option a) — marketing pages still reference the Community Wall
  // as our USP, and the routes stay live at /v3/community + /v3/community/forum
  // for direct-link access. Simply not competing with Pipeline for attention.
  { kind: "leaf",  to: "/v3/coach",     label: "AI Center",      icon: Sparkles,   testid: "v3-nav-ai-center", ai: true },
  { kind: "leaf",  to: "/v3/business",  label: "Business",       icon: Briefcase,  testid: "v3-nav-business" },
  { kind: "leaf",  to: "/v3/settings",  label: "Settings",       icon: SettingsIcon, testid: "v3-nav-settings" },
  { kind: "leaf",  to: "/v3/support",   label: "Help & Support", icon: LifeBuoy,   testid: "v3-nav-support" },
  { kind: "leaf",  to: "/v3/admin",     label: "Administration", icon: Shield,     testid: "v3-nav-admin" },
  { kind: "leaf",  to: "/v3/admin/beta", label: "Beta Cockpit",   icon: ShieldCheck, testid: "v3-nav-admin-beta", badge: "NEW" },
];

const LEAF_INDEX = (() => {
  const m = new Map();
  for (const n of NAV) {
    if (n.kind === "leaf") m.set(n.to, n);
    if (n.kind === "group") for (const c of n.items) m.set(c.to, c);
  }
  return m;
})();

// ──────────────────────────────────────────────────────────────
// Persistence
// ──────────────────────────────────────────────────────────────
const LS = {
  RAIL:   "v3_shell_rail_v1",
  GROUPS: "v3_shell_groups_open_v1",
  FAVS:   "v3_shell_favs_v1",
};
const readJson = (k, fb) => {
  try { const v = localStorage.getItem(k); return v ? JSON.parse(v) : fb; } catch { return fb; }
};
const writeJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

// ──────────────────────────────────────────────────────────────
// Shell
// ──────────────────────────────────────────────────────────────
export default function V3Shell() {
  const { user, logout } = useAuth?.() || { user: null, logout: () => {} };
  const loc = useLocation();
  const nav = useNavigate();

  const [railed, setRailed] = useState(() => readJson(LS.RAIL, false));
  const [groups, setGroups] = useState(() => readJson(LS.GROUPS, { crm: true, outreach: false, intel: true, strategy: true, buyers: true, tools: false, growth: false, community: false }));
  const [favs, setFavs] = useState(() => readJson(LS.FAVS, []));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => writeJson(LS.RAIL, railed), [railed]);
  useEffect(() => writeJson(LS.GROUPS, groups), [groups]);
  useEffect(() => writeJson(LS.FAVS, favs), [favs]);
  useEffect(() => { setDrawerOpen(false); }, [loc.pathname]);

  const effectiveGroups = useMemo(() => {
    const out = { ...groups };
    for (const n of NAV) {
      if (n.kind !== "group") continue;
      if (n.items.some((i) => loc.pathname === i.to || loc.pathname.startsWith(i.to + "/"))) {
        out[n.id] = true;
      }
    }
    return out;
  }, [groups, loc.pathname]);

  const toggleGroup = (id) => setGroups((g) => ({ ...g, [id]: !effectiveGroups[id] }));
  const toggleFav = (to) => setFavs((f) => (f.includes(to) ? f.filter((x) => x !== to) : [...f, to]));

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
      if (e.key === "Escape") setSearchOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <ActivePropertyProvider>
    <div className="min-h-screen flex bg-slate-50 text-slate-900 v3-app">
      {drawerOpen && (
        <button
          type="button"
          aria-label="Close menu"
          data-testid="v3-sidebar-overlay"
          className="lg:hidden fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-30"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <Sidebar
        railed={railed}
        setRailed={setRailed}
        drawerOpen={drawerOpen}
        groups={effectiveGroups}
        toggleGroup={toggleGroup}
        favs={favs}
        toggleFav={toggleFav}
        user={user}
      />

      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar
          onOpenMobileNav={() => setDrawerOpen(true)}
          onOpenSearch={() => setSearchOpen(true)}
          user={user}
          logout={logout}
          nav={nav}
        />
        <DemoModeBanner />
        <main data-testid="v3-main" className="flex-1 min-w-0">
          <div className="mx-auto max-w-[1600px] px-4 md:px-8 py-8">
            <Outlet />
          </div>
        </main>
      </div>

      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} nav={nav} />}
      <DealWonModal />
      <SupportWidget />
      <JarvisFAB />
      <BetaFrictionFab />
      <OnboardingTour />
    </div>
    </ActivePropertyProvider>
  );
}

// ──────────────────────────────────────────────────────────────
// Sidebar — dark charcoal, gold accent
// ──────────────────────────────────────────────────────────────
function Sidebar({ railed, setRailed, drawerOpen, groups, toggleGroup, favs, toggleFav, user }) {
  return (
    <aside
      data-testid="v3-sidebar"
      data-railed={railed ? "1" : "0"}
      className={[
        "text-slate-200 flex flex-col shrink-0",
        // Mobile: fixed drawer (off-screen when closed via -translate-x-full).
        // Desktop (lg+): static in the flex row.
        "fixed inset-y-0 left-0 z-40 lg:static",
        "transition-[transform,width] duration-200 ease-out",
        drawerOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
        railed ? "w-[68px]" : "w-[264px]",
      ].join(" ")}
      style={{
        backgroundColor: "#0B1220",
        backgroundImage:
          "radial-gradient(ellipse at top left, rgba(79,70,229,0.14), transparent 55%), radial-gradient(ellipse at bottom right, rgba(245,158,11,0.06), transparent 55%)",
        boxShadow: "inset -1px 0 0 rgba(255,255,255,0.05)",
      }}
    >
      {/* Brand */}
      <div
        data-testid="v3-sidebar-brand"
        className={["flex items-center justify-center shrink-0",
                    railed ? "h-16 px-2" : "h-20 px-3"].join(" ")}
      >
        {railed ? (
          // Collapsed rail — small monogram square on the cream plate style
          <Link
            to="/v3"
            data-testid="v3-sidebar-brand-link"
            className="h-10 w-10 rounded-xl grid place-items-center shadow-lg ring-1 ring-amber-200/40 focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{
              backgroundColor: "#FDF8ED",
              backgroundImage: "linear-gradient(135deg, #FEFCF3 0%, #FDF3D8 100%)",
            }}
            aria-label="iSpeedToClose home"
          >
            <img
              src="/brand/logo-badge.png?v=2"
              alt="iSpeedToClose"
              className="h-7 w-7 select-none pointer-events-none"
              draggable={false}
            />
          </Link>
        ) : (
          // Expanded — full color lockup on a warm cream "brand plate" that
          // makes both the navy blue AND amber gold of the logo pop against
          // the dark charcoal sidebar background.
          <Link
            to="/v3"
            data-testid="v3-sidebar-brand-link"
            className="group w-full rounded-xl px-3 py-2.5 flex items-center justify-center shadow-lg ring-1 ring-amber-200/40 hover:ring-amber-300/60 transition-all focus:outline-none focus:ring-2 focus:ring-amber-400"
            style={{
              backgroundColor: "#FDF8ED",
              backgroundImage: "linear-gradient(135deg, #FEFCF3 0%, #FDF3D8 100%)",
              boxShadow:
                "0 8px 24px -8px rgba(245,158,11,0.25), 0 2px 6px -1px rgba(15,23,42,0.35), inset 0 1px 0 rgba(255,255,255,0.7)",
            }}
            aria-label="iSpeedToClose home"
          >
            <img
              src="/brand/logo-full-color.png?v=1"
              alt="iSpeedToClose"
              className="h-11 w-auto max-w-full select-none pointer-events-none group-hover:scale-[1.02] transition-transform"
              draggable={false}
            />
          </Link>
        )}
      </div>

      {/* subtle divider */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />

      {/* Favorites */}
      <div className="px-2.5 pt-4">
        {!railed && (
          <div className="px-2 pb-1.5 text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">
            Favorites
          </div>
        )}
        <div data-testid="v3-sidebar-favorites" className="space-y-1">
          {favs.length === 0 && !railed && (
            <div data-testid="v3-favorites-empty"
                 className="mx-2 px-2 py-2 rounded-md border border-dashed border-white/10 text-[11px] text-slate-500 leading-snug">
              Star any item below to pin it here.
            </div>
          )}
          {favs.map((path) => {
            const leaf = LEAF_INDEX.get(path);
            if (!leaf) return null;
            return <NavItem key={`fav-${path}`} item={leaf} railed={railed} isFav
                            onToggleFav={() => toggleFav(path)} />;
          })}
        </div>
      </div>

      {!railed && <div className="mx-4 mt-3 h-px bg-white/5" />}

      {/* Primary navigation */}
      <nav aria-label="Primary"
           className="flex-1 overflow-y-auto px-2.5 py-3 space-y-1 scrollbar-thin">
        {NAV.map((n, idx) => {
          if (n.kind === "section") {
            return (
              <SectionHeader
                key={`section-${n.testid || idx}`}
                label={n.label}
                icon={n.icon}
                railed={railed}
                testid={n.testid}
              />
            );
          }
          if (n.kind === "leaf") {
            return (
              <NavItem
                key={n.to}
                item={n}
                railed={railed}
                isFav={favs.includes(n.to)}
                onToggleFav={() => toggleFav(n.to)}
              />
            );
          }
          return (
            <NavGroup
              key={n.id}
              group={n}
              open={groups[n.id]}
              onToggle={() => toggleGroup(n.id)}
              railed={railed}
              favs={favs}
              onToggleFav={toggleFav}
            />
          );
        })}
      </nav>

      {/* User chip + Collapse */}
      <div className="mt-auto border-t border-white/5 p-2.5 space-y-1">
        {!railed && user && (
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-md">
            <div className="h-8 w-8 rounded-full grid place-items-center text-[11px] font-bold text-white"
                 style={{ backgroundImage: "linear-gradient(135deg, #F59E0B, #B45309)" }}>
              {(user?.name || user?.email || "?").split(/\s+/).map(s=>s[0]).filter(Boolean).slice(0,2).join("").toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-white truncate">
                {user?.name || "You"}
              </div>
              <div className="text-[10px] text-slate-500 truncate">
                {user?.email}
              </div>
            </div>
          </div>
        )}
        <button
          type="button"
          data-testid="v3-sidebar-rail-toggle"
          aria-label={railed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setRailed((r) => !r)}
          className="hidden lg:inline-flex items-center gap-2 rounded-md px-2 py-1.5 text-[11px] font-medium text-slate-400 hover:bg-white/5 hover:text-white w-full transition-colors"
        >
          {railed ? <ChevronsRight className="w-4 h-4" /> : <><ChevronsLeft className="w-4 h-4" /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  );
}

// ──────────────────────────────────────────────────────────────
// SectionHeader — small-caps divider that groups the workflow zones
// (Acquisitions · Dispositions · General). Icon + label render at full
// size when the sidebar is expanded; when railed, it collapses to a
// centered mini-icon so the visual rhythm of the rail is preserved.
// ──────────────────────────────────────────────────────────────
function SectionHeader({ label, icon: Icon, railed, testid }) {
  if (railed) {
    return (
      <div
        className="flex items-center justify-center py-2.5 mt-3 first:mt-0"
        title={label}
        data-testid={testid}
      >
        <div className="h-px w-6 bg-white/10" />
        {Icon && <Icon className="w-3.5 h-3.5 mx-1.5 text-amber-500/70" />}
        <div className="h-px w-6 bg-white/10" />
      </div>
    );
  }
  return (
    <div
      className="flex items-center gap-2 px-2 pt-5 pb-1.5 first:pt-2"
      data-testid={testid}
    >
      {Icon && <Icon className="w-3.5 h-3.5 text-amber-500/80" />}
      <span className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-500/80">
        {label}
      </span>
      <div className="flex-1 h-px bg-white/8" />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// NavGroup — collapsible
// ──────────────────────────────────────────────────────────────
function NavGroup({ group, open, onToggle, railed, favs, onToggleFav }) {
  const loc = useLocation();
  const containsActive = group.items.some(
    (i) => loc.pathname === i.to || loc.pathname.startsWith(i.to + "/"),
  );

  if (railed) {
    const Icon = group.icon;
    const firstItem = group.items[0];
    return (
      <NavLink
        to={firstItem.to}
        title={group.label}
        data-testid={group.testid}
        className={({ isActive }) =>
          `relative flex items-center justify-center h-10 w-full rounded-lg transition-all ${
            isActive || containsActive
              ? "bg-white/10 text-amber-400"
              : "text-slate-400 hover:bg-white/5 hover:text-white"
          }`
        }
      >
        {(containsActive) && (
          <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-amber-400" />
        )}
        <Icon className="w-4 h-4" />
      </NavLink>
    );
  }

  const Chevron = open ? ChevronDown : ChevronRight;
  const Icon = group.icon;
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        data-testid={group.testid}
        className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] transition-all ${
          containsActive
            ? "text-amber-300 font-semibold bg-white/[0.03]"
            : "text-slate-300 hover:bg-white/5 hover:text-white"
        }`}
      >
        <Icon className={`w-4 h-4 shrink-0 ${containsActive ? "text-amber-400" : ""}`} />
        <span className="flex-1 text-left">{group.label}</span>
        <Chevron className="w-3.5 h-3.5 text-slate-500" />
      </button>
      <div
        data-testid={`${group.testid}-items`}
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="overflow-hidden">
          <div className="mt-1 ml-4 pl-2.5 border-l border-white/10 space-y-0.5 py-0.5">
            {group.items.map((i) => (
              <NavItem
                key={i.to}
                item={i}
                railed={false}
                small
                isFav={favs.includes(i.to)}
                onToggleFav={() => onToggleFav(i.to)}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// NavItem — one link row (leaf)
// ──────────────────────────────────────────────────────────────
function NavItem({ item, railed, small, isFav, onToggleFav }) {
  const Icon = item.icon;
  // Optional coloured dot for engine/strategy leaves
  const theme = item.slug ? MODULE_THEME[item.slug] : null;
  const dotHex = theme?.hex;
  // Auto-carry the active property into Intelligence Engine links so the
  // engine loads pre-scoped — no more "Add a property" wall on click.
  const { propertyId } = useActiveProperty();
  const carriesProperty = /^\/v3\/intel\//.test(item.to || "");
  const targetTo = carriesProperty && propertyId
    ? `${item.to}?property_id=${propertyId}`
    : item.to;

  // Live badge for nav items that expose a `liveBadgeSource` — one-shot
  // fetch on mount. Currently only "readyToBlast" is wired up; add more
  // sources as the operator's dashboards grow.
  const [liveBadge, setLiveBadge] = React.useState(null);
  React.useEffect(() => {
    if (item.liveBadgeSource !== "readyToBlast") return;
    let cancelled = false;
    api.get("/v3/disposition/ready-to-blast?limit=1")
      .then(({ data }) => {
        if (cancelled) return;
        const n = data?.totals?.deals ?? 0;
        setLiveBadge(n > 0 ? n : null);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [item.liveBadgeSource]);

  return (
    <div className="group relative">
      <NavLink
        to={targetTo}
        end={item.end}
        title={item.label}
        data-testid={item.testid}
        className={({ isActive }) =>
          [
            "relative flex items-center rounded-lg transition-all",
            railed
              ? "justify-center h-10 w-full text-slate-400 hover:text-white hover:bg-white/5"
              : `gap-2.5 px-2.5 py-2 ${small ? "text-[12.5px]" : "text-[13px]"} text-slate-300 hover:bg-white/5 hover:text-white`,
            isActive
              ? "!bg-indigo-500/[0.14] !text-indigo-300 font-semibold ring-1 ring-inset ring-indigo-400/25"
              : "",
          ].join(" ")
        }
      >
        {({ isActive }) => (
          <>
            {isActive && !railed && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-4 w-[3px] rounded-r bg-indigo-400" />
            )}
            {isActive && railed && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-r bg-indigo-400" />
            )}
            {dotHex && !railed && (
              <span aria-hidden="true"
                    className="w-1.5 h-1.5 rounded-full shrink-0 ring-1 ring-white/10"
                    style={{ backgroundColor: dotHex }} />
            )}
            <Icon className={`w-4 h-4 shrink-0 ${isActive ? "text-indigo-400" : ""}`} />
            {!railed && <span className="truncate flex-1">{item.label}</span>}
            {item.ai && !railed && (
              <span className="text-[9px] font-black tracking-widest uppercase px-1.5 py-0.5 rounded-full ml-auto"
                    style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)", color: "white" }}>
                AI
              </span>
            )}
            {liveBadge != null && !railed && (
              <span
                data-testid={`${item.testid}-live-badge`}
                className="text-[10px] font-black tracking-wide uppercase px-1.5 py-0.5 rounded-full ml-auto bg-gradient-to-br from-amber-500 to-amber-700 text-white shadow-sm tabular-nums"
              >
                {liveBadge}
              </span>
            )}
            {liveBadge != null && railed && (
              <span
                aria-hidden="true"
                className="absolute top-0.5 right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-[#0B1220]"
              />
            )}
          </>
        )}
      </NavLink>
      {!railed && (
        <button
          type="button"
          onClick={(e) => { e.preventDefault(); onToggleFav?.(); }}
          aria-label={isFav ? "Unpin from favorites" : "Pin to favorites"}
          data-testid={`${item.testid}-fav`}
          className={`absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 transition-opacity ${
            isFav ? "opacity-100 text-amber-400" : "opacity-0 group-hover:opacity-100 text-slate-500 hover:text-amber-400"
          }`}
        >
          {isFav ? <Star className="w-3.5 h-3.5 fill-current" /> : <StarOff className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// TopBar — white, thin gold accent line
// ──────────────────────────────────────────────────────────────
function TopBar({ onOpenMobileNav, onOpenSearch, user, logout, nav }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [wsOpen, setWsOpen] = useState(false);

  const initials = (user?.name || user?.email || "?")
    .split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <header
      data-testid="v3-topbar"
      className="h-16 bg-white/95 backdrop-blur-xl sticky top-0 z-20 flex items-center gap-3 px-4 md:px-8 relative"
    >
      {/* Thin gold accent line */}
      <span aria-hidden="true"
            className="absolute left-0 right-0 bottom-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, rgba(245,158,11,0) 0%, rgba(245,158,11,0.55) 30%, rgba(245,158,11,0.55) 70%, rgba(245,158,11,0) 100%)",
            }} />
      <span aria-hidden="true"
            className="absolute left-0 right-0 -bottom-px h-px bg-slate-200" />

      {/* Mobile hamburger */}
      <button type="button" onClick={onOpenMobileNav} aria-label="Open menu"
              data-testid="v3-topbar-mobile-nav"
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-600 hover:bg-slate-100">
        <Menu className="w-5 h-5" />
      </button>

      {/* Global search */}
      <button
        type="button"
        onClick={onOpenSearch}
        data-testid="v3-topbar-search"
        className="flex-1 max-w-xl flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 hover:bg-white hover:border-amber-300 hover:shadow-sm px-3.5 py-2 text-left text-[13px] text-slate-500 transition-all"
      >
        <Search className="w-4 h-4 text-slate-400" />
        <span className="flex-1 truncate">Search properties, buyers, deals…</span>
        <kbd className="hidden md:inline text-[10px] font-mono bg-white border border-slate-200 rounded px-1.5 py-0.5 text-slate-400 shadow-sm">
          ⌘K
        </kbd>
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        <WorkspaceFocusChip />
        <SeminarCountdownChip variant="light" testid="v3-topbar-seminar-countdown" />
        <ActivePropertyPill />
        <CreditsChip />

        <IconButton testid="v3-topbar-notifications" label="Notifications"
                    onClick={() => nav("/v3/notifications")}
                    badge>
          <Bell className="w-4 h-4" />
        </IconButton>

        {/* Workspace selector */}
        <div className="relative">
          <button type="button" data-testid="v3-topbar-workspace"
                  onClick={() => setWsOpen((v) => !v)}
                  className="hidden md:inline-flex items-center gap-2 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 pl-2.5 pr-2 py-1.5 text-[12px] font-semibold text-slate-700 transition-all">
            <Building2 className="w-3.5 h-3.5 text-amber-500" />
            <span className="truncate max-w-[160px]">
              {user?.brand_company || "My Workspace"}
            </span>
            <CaretDown className="w-3 h-3 text-slate-400" />
          </button>
          {wsOpen && (
            <div className="absolute right-0 top-full mt-2 w-64 rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 py-1 z-30"
                 data-testid="v3-topbar-workspace-menu">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-slate-400 font-bold">Workspace</div>
              <div className="px-3 py-1 text-sm font-semibold text-slate-900 truncate">{user?.brand_company || "My Workspace"}</div>
              <div className="px-3 pb-2 text-xs text-slate-500 truncate">{user?.email}</div>
              <div className="border-t border-slate-100 py-1">
                <button type="button" disabled
                        className="w-full text-left px-3 py-1.5 text-xs text-slate-400 cursor-not-allowed">
                  Switch workspace (soon)
                </button>
              </div>
            </div>
          )}
        </div>

        {/* First Closer badge — worn by Early Adopter Program members */}
        {user?.founding_member && (
          <div className="hidden md:inline-flex items-center gap-1 rounded-full text-[9px] font-black tracking-widest uppercase px-2 py-1 text-white shadow-md"
               data-testid="v3-topbar-first-closer-badge"
               title={`First Closer #${user.founding_cohort || "—"} · 40% off for life`}
               style={{ backgroundImage: "linear-gradient(90deg, #F59E0B, #8B5CF6)" }}>
            <Crown className="w-3 h-3" /> First Closer
          </div>
        )}

        {/* Profile */}
        <div className="relative">
          <button type="button" data-testid="v3-topbar-profile"
                  onClick={() => setProfileOpen((v) => !v)}
                  className="inline-flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-slate-100 transition-colors">
            <span className="h-8 w-8 rounded-full grid place-items-center text-white text-[11px] font-bold shadow-md shadow-amber-500/20"
                  style={{ backgroundImage: "linear-gradient(135deg, #FBBF24, #B45309)" }}>
              {initials || "U"}
            </span>
            <CaretDown className="w-3 h-3 text-slate-400" />
          </button>
          {profileOpen && (
            <div className="absolute right-0 top-full mt-2 w-60 rounded-xl border border-slate-200 bg-white shadow-xl shadow-slate-900/5 py-1 z-30"
                 data-testid="v3-topbar-profile-menu">
              <div className="px-3 py-2">
                <div className="text-sm font-semibold text-slate-900 truncate">{user?.name || "You"}</div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
              <div className="border-t border-slate-100 py-1">
                <button type="button" onClick={() => { setProfileOpen(false); nav("/v3/admin"); }}
                        data-testid="v3-topbar-profile-settings"
                        className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                  Account settings
                </button>
                <button type="button" onClick={() => { setProfileOpen(false); logout?.(); nav("/login"); }}
                        data-testid="v3-topbar-profile-logout"
                        className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
                  <LogOut className="w-3.5 h-3.5" /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

function IconButton({ children, onClick, testid, label, badge }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} data-testid={testid}
            className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors">
      {children}
      {badge && (
        <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 ring-2 ring-white" />
      )}
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// CreditsChip — top-bar wallet showing current marketplace credit
// balance with a one-tap link to /v3/crm/marketplace. Polls once
// per mount + on route changes so the chip stays fresh after a
// buy/redeem without needing a global state store.
// ──────────────────────────────────────────────────────────────
function CreditsChip() {
  const [balance, setBalance] = useState(null);
  const [tick, setTick] = useState(0);
  const { pathname } = useLocation();
  useEffect(() => {
    let alive = true;
    api.get("/marketplace/balance")
      .then((r) => { if (alive) setBalance(r.data?.balance ?? 0); })
      .catch(() => { if (alive) setBalance(null); });
    return () => { alive = false; };
  }, [pathname, tick]);
  // Any component (e.g. MarketplacePage after subscribe/redeem) can dispatch
  // `window.dispatchEvent(new Event("credits-changed"))` to force an immediate
  // refetch — no route change required.
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    window.addEventListener("credits-changed", bump);
    return () => window.removeEventListener("credits-changed", bump);
  }, []);
  const bal = balance ?? 0;
  const low = balance != null && balance < 10;
  return (
    <Link
      to="/v3/crm/marketplace"
      data-testid="v3-topbar-credits-chip"
      title={`${bal} lead credits · Click to visit the marketplace`}
      className={`hidden md:inline-flex items-center gap-1.5 rounded-lg border pl-2 pr-2.5 py-1.5 text-[12px] font-bold tabular-nums transition-all ${
        low
          ? "border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800"
          : "border-slate-200 hover:border-amber-300 hover:bg-amber-50/40 text-slate-700"
      }`}
    >
      <Coins className={`w-3.5 h-3.5 ${low ? "text-amber-600" : "text-amber-500"}`} />
      <span data-testid="v3-topbar-credits-value">{bal}</span>
      <span className="hidden lg:inline text-slate-400 font-normal text-[10.5px]">credits</span>
    </Link>
  );
}

// ──────────────────────────────────────────────────────────────
// SearchOverlay — Cmd+K quick nav
// ──────────────────────────────────────────────────────────────
function SearchOverlay({ onClose, nav }) {
  const [q, setQ] = useState("");
  const leaves = useMemo(() => Array.from(LEAF_INDEX.values()), []);
  const matches = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return leaves.slice(0, 8);
    return leaves.filter((l) => l.label.toLowerCase().includes(query)).slice(0, 20);
  }, [q, leaves]);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-start justify-center pt-24 px-4 animate-in fade-in duration-150"
      onClick={onClose}
      data-testid="v3-search-overlay"
    >
      <div
        className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-slate-900/20 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 border-b border-slate-100 px-4">
          <Search className="w-4 h-4 text-amber-500" />
          <input autoFocus type="text" value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Jump to a page or search…"
                 className="flex-1 py-3.5 text-[14px] outline-none placeholder-slate-400"
                 data-testid="v3-search-input" />
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" aria-label="Close search">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="max-h-80 overflow-y-auto py-1" data-testid="v3-search-results">
          {matches.length === 0 && (
            <div className="px-4 py-6 text-sm text-slate-400">No matches. Try another search term.</div>
          )}
          {matches.map((m) => (
            <button
              key={m.to} type="button"
              onClick={() => { onClose(); nav(m.to); }}
              className="w-full text-left flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-amber-50/60 transition-colors"
            >
              <m.icon className="w-4 h-4 text-slate-400" />
              <span className="flex-1 truncate font-medium">{m.label}</span>
              <span className="text-[11px] text-slate-400 font-mono truncate">{m.to}</span>
            </button>
          ))}
        </div>
        <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400 flex items-center gap-3">
          <span><kbd className="font-mono bg-slate-100 rounded px-1">↵</kbd> open</span>
          <span><kbd className="font-mono bg-slate-100 rounded px-1">Esc</kbd> close</span>
          <span className="ml-auto text-amber-600 font-semibold">Powered by iSpeed AI</span>
        </div>
      </div>
    </div>
  );
}
